/**
 * Branches without a model, and the memory that lets one be explained afterwards.
 *
 * With the AI companion off — which is the default — this is where a branch set comes
 * from. `@hc/shared`'s `heuristics/` does all of the thinking; everything here is the
 * adaptation between a `SessionState` and the engine's input, plus the one piece of
 * bookkeeping the engine cannot do for us.
 *
 * ## Why there is a memory at all
 *
 * `generateHeuristicBranchSet` hands back three things per branch: the `BranchSuggestion`
 * in the wire shape, the library `moveId` that authored it, and the long explanation
 * already filled in against this exact pair of chords. Only the first of those has
 * anywhere to live — a `BranchSet` is the wire shape and nothing else, deliberately, so
 * that nothing downstream can tell which engine wrote a card.
 *
 * The other two are read later and elsewhere: an expanded card asks for the paragraph
 * some seconds after the set arrived, and the history detail asks about a move made three
 * chords ago. So they are kept here, keyed on the suggestion id, and `sessionHost`'s
 * `fetchExplanation` reads them instead of reaching for `/api/explain`.
 *
 * The `moveId` rides along beside the resolved text because it is the stable library
 * identifier `explainMove` takes — the seam for a caller that has the id and needs the
 * paragraph re-derived against different chords. Nothing does that yet; keeping the id is
 * what makes it possible without a second generation pass.
 *
 * ## Why it is a module rather than session state
 *
 * The same argument `useExplanation`'s cache makes: this is not part of the session, it
 * must not be persisted into a sketch, and one copy has to serve the card, the sheet and
 * the history detail. A page load empties it, which is correct — after a reload the
 * paragraph is not available and the sheets say so rather than inventing one.
 */

import {
  currentNode,
  generateHeuristicBranchSet,
  toCanonical,
  type BranchCurrentChord,
  type BranchSuggestion,
  type HeuristicBranch,
  type HeuristicPathEntry,
  type MovementKind,
  type PitchClass,
  type PreferenceHints,
  type SessionState,
} from '@hc/shared';

/**
 * How many suggestions keep their explanation.
 *
 * Generous by two orders of magnitude for the case it exists for — a player reading the
 * cards in front of them — and bounded because the engine is free, so "something else"
 * costs nothing and can be asked for all afternoon. Eviction is oldest-first: a
 * suggestion this far back is off the end of any history anybody is going to open.
 */
export const HEURISTIC_MEMORY_LIMIT = 500;

interface RememberedBranch {
  moveId: string;
  explanation: string;
}

const remembered = new Map<string, RememberedBranch>();

/** File a generated set, so its paragraphs can be read when somebody asks for one. */
export function rememberHeuristicBranches(branches: readonly HeuristicBranch[]): void {
  for (const branch of branches) {
    remembered.set(branch.suggestion.id, {
      moveId: branch.moveId,
      explanation: branch.explanation,
    });
    // `Map` iterates in insertion order, so the first key is the oldest.
    while (remembered.size > HEURISTIC_MEMORY_LIMIT) {
      const oldest = remembered.keys().next().value;
      if (oldest === undefined) break;
      remembered.delete(oldest);
    }
  }
}

/** The paragraph for a suggestion this engine wrote, or `null` for anything else. */
export function heuristicExplanation(suggestionId: string | null | undefined): string | null {
  if (!suggestionId) return null;
  return remembered.get(suggestionId)?.explanation ?? null;
}

/** The library move a suggestion came from — the id `explainMove` takes. */
export function heuristicMoveId(suggestionId: string | null | undefined): string | null {
  if (!suggestionId) return null;
  return remembered.get(suggestionId)?.moveId ?? null;
}

/** Test-only, for the same reason `resetExplanationCache` is: fixture ids collide. */
export function resetHeuristicBranchMemory(): void {
  remembered.clear();
}

// ---------------------------------------------------------------------------
// Generating
// ---------------------------------------------------------------------------

export interface HeuristicBranchRequest {
  branchCount: number;
  preferenceHints: PreferenceHints;
  takenKinds: readonly MovementKind[];
  ignoredKinds: readonly MovementKind[];
  /** Sets already on offer for this chord. What makes "something else" mean something. */
  excludePitchClassSets?: ReadonlyArray<readonly PitchClass[]>;
  /**
   * The engine's only entropy, and its ids. Both are required on the engine's own
   * signature so that purity is a fact about the type rather than a convention; they are
   * required here too rather than defaulted, because this module sits one layer above it
   * and defaulting would put the convention back.
   */
  rng: () => number;
  mintId: () => string;
}

/**
 * One step of history, as the engine reads it.
 *
 * `rootPc` is the whole of the difference from `BranchPathEntry`, and it is here rather
 * than on the wire because nothing server-side reads a root and the server's cache key is
 * the entire request body. The engine cannot recover one: `bassPc` is the sounding bass,
 * and `symbol` is display-only. The session path holds whole `CanonicalChord`s, so
 * passing it is free.
 */
function pathEntry(chord: {
  symbol: string;
  pitchClasses: readonly PitchClass[];
  rootPc: PitchClass;
  bassPc: PitchClass;
}): HeuristicPathEntry {
  return {
    symbol: chord.symbol,
    pitchClasses: [...chord.pitchClasses],
    rootPc: chord.rootPc,
    bassPc: chord.bassPc,
  };
}

/**
 * A branch set for the current chord, generated on this device.
 *
 * The shape of the input mirrors `buildBranchRequest` exactly where the two overlap —
 * `path` is the history *before* the current chord, the tallies are the session's — so
 * that turning the companion on or off between two chords changes where the suggestions
 * come from and nothing about what they are being asked for.
 *
 * `perspectives` and `intention` have no counterpart here and are not smuggled in. They
 * are prompt material: instructions written for a reader. The engine has nothing that
 * reads, which is why the controls that set them are hidden while it is the one answering.
 */
export function generateBranchesFor(
  state: SessionState,
  request: HeuristicBranchRequest,
): HeuristicBranch[] {
  const node = currentNode(state);
  if (!node) return [];

  const current: BranchCurrentChord = {
    symbol: node.chord.symbol,
    pitchClasses: [...node.chord.pitchClasses],
    rootPc: node.chord.rootPc,
    bassPc: node.chord.bassPc,
  };

  return generateHeuristicBranchSet({
    current,
    keyEstimate: state.keyEstimate,
    path: state.path.slice(0, -1).map((entry) => pathEntry(entry.chord)),
    adventurousness: state.adventurousness,
    branchCount: request.branchCount,
    takenKinds: request.takenKinds,
    ignoredKinds: request.ignoredKinds,
    preferenceHints: request.preferenceHints,
    ...(request.excludePitchClassSets && request.excludePitchClassSets.length > 0
      ? { excludePitchClassSets: request.excludePitchClassSets }
      : {}),
    rng: request.rng,
    mintId: request.mintId,
  });
}

/**
 * An id for a suggestion this device authored.
 *
 * CLAUDE.md's rule is that suggestion ids are minted by whichever engine wrote the set,
 * never asked of a model, and the reason behind it is **stability**: branch matching and
 * the explanation memory both key on the id, so it has to be unique and it has to survive
 * a retry. A UUID satisfies that here exactly as `randomUUID` does on the server. The
 * fallback is for the one environment that has neither — an old Safari with no
 * `crypto.randomUUID` — where a timestamp and a random suffix are still unique enough for
 * a map that lives inside one page load.
 */
export function newSuggestionId(): string {
  const cryptoRef = globalThis.crypto as { randomUUID?: () => string } | undefined;
  if (typeof cryptoRef?.randomUUID === 'function') return cryptoRef.randomUUID();
  return `hb-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

/** The pitch-class sets a set is already offering, for "show me something else". */
export function offeredPitchClassSets(
  suggestions: readonly BranchSuggestion[],
): Array<readonly PitchClass[]> {
  return suggestions.map((suggestion) => toCanonical(suggestion.chord).pitchClasses);
}
